import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import datapoints from '../../data/datapoints.json';
import {
  EVENT_CLASSES,
  EVENT_TYPES,
  DATA_COLLECTION_TYPES,
  SUBMISSION_LIMIT_TYPES,
  GAME_MODES,
} from '../utils/eventPlanConstants';
import { getAssetPickerLabel } from '../utils/assetUtils';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function toUtcInputValue(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

function fromUtcInputValue(value) {
  if (!value) return '';
  return `${value}:00.000Z`;
}

export default function EventPlanEditDialog({ open, onClose, eventPlan, assets = [], onSave, saving }) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(null);
  const [advancedJson, setAdvancedJson] = useState('');
  const [advancedError, setAdvancedError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTab(0);
    if (eventPlan?.definition) {
      setForm(JSON.parse(JSON.stringify(eventPlan.definition)));
      setAdvancedJson(JSON.stringify({
        donateDayListJson: eventPlan.definition.donateDayListJson ?? null,
        blueprintDayListJson: eventPlan.definition.blueprintDayListJson ?? null,
        eventTierListJson: eventPlan.definition.eventTierListJson ?? null,
      }, null, 2));
    } else {
      const id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `new-${Date.now()}`;
      setForm({
        id,
        eventClass: 'Bronze',
        enabled: false,
        showInUpcoming: true,
        title: 'New event',
        description: '',
        eventType: 'Fishing',
        dataPoint: 'CaughtFishRarity',
        dataCollectionType: 'Sum',
        submissionLimitType: 'Daily',
        eventSubmissionLimit: 12,
        leaderboardEnabled: true,
        leaderboardLength: 0,
        rewardTierInfos: [],
        attemptLength: 60,
        gameMode: 'None',
        dataPointInAttempt: 'FoliageCut',
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        donateDayListJson: null,
        blueprintDayListJson: null,
        eventTierListJson: null,
        targetIds: '',
      });
      setAdvancedJson('{\n  "donateDayListJson": null,\n  "blueprintDayListJson": null,\n  "eventTierListJson": null\n}');
    }
    setAdvancedError('');
  }, [eventPlan, open]);

  const assetOptions = useMemo(
    () => assets.map((asset) => ({ ...asset, label: getAssetPickerLabel(asset) })),
    [assets]
  );

  if (!form) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateTier = (index, patch) => {
    setForm((prev) => {
      const tiers = [...(prev.rewardTierInfos || [])];
      tiers[index] = { ...tiers[index], ...patch };
      return { ...prev, rewardTierInfos: tiers };
    });
  };

  const updateTierReward = (tierIndex, rewardIndex, patch) => {
    setForm((prev) => {
      const tiers = [...(prev.rewardTierInfos || [])];
      const rewards = [...(tiers[tierIndex]?.rewards || [])];
      rewards[rewardIndex] = { ...rewards[rewardIndex], ...patch };
      tiers[tierIndex] = { ...tiers[tierIndex], rewards };
      return { ...prev, rewardTierInfos: tiers };
    });
  };

  const addTier = () => {
    setForm((prev) => ({
      ...prev,
      rewardTierInfos: [
        ...(prev.rewardTierInfos || []),
        { minPosition: 0, maxPosition: 0, rewards: [{ amount: 1, targetId: '' }] },
      ],
    }));
  };

  const removeTier = (index) => {
    setForm((prev) => ({
      ...prev,
      rewardTierInfos: (prev.rewardTierInfos || []).filter((_, i) => i !== index),
    }));
  };

  const addTierReward = (tierIndex) => {
    setForm((prev) => {
      const tiers = [...(prev.rewardTierInfos || [])];
      const rewards = [...(tiers[tierIndex]?.rewards || []), { amount: 1, targetId: '' }];
      tiers[tierIndex] = { ...tiers[tierIndex], rewards };
      return { ...prev, rewardTierInfos: tiers };
    });
  };

  const removeTierReward = (tierIndex, rewardIndex) => {
    setForm((prev) => {
      const tiers = [...(prev.rewardTierInfos || [])];
      tiers[tierIndex] = {
        ...tiers[tierIndex],
        rewards: (tiers[tierIndex]?.rewards || []).filter((_, i) => i !== rewardIndex),
      };
      return { ...prev, rewardTierInfos: tiers };
    });
  };

  const handleSave = () => {
    let advanced;
    try {
      advanced = JSON.parse(advancedJson);
      setAdvancedError('');
    } catch (err) {
      setAdvancedError(err.message);
      setTab(3);
      return;
    }

    onSave({
      ...form,
      donateDayListJson: advanced.donateDayListJson ?? null,
      blueprintDayListJson: advanced.blueprintDayListJson ?? null,
      eventTierListJson: advanced.eventTierListJson ?? null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{eventPlan ? `Edit: ${form.title}` : 'New event plan'}</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Overview" />
          <Tab label="Scoring" />
          <Tab label="Rewards" />
          <Tab label="Advanced" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Stack spacing={2}>
            <TextField label="Event ID" value={form.id} disabled fullWidth size="small" />
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description (HTML allowed)"
              value={form.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              fullWidth
              multiline
              minRows={4}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Event class"
                value={form.eventClass}
                onChange={(e) => updateField('eventClass', e.target.value)}
                fullWidth
              >
                {EVENT_CLASSES.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Event type"
                value={form.eventType}
                onChange={(e) => updateField('eventType', e.target.value)}
                fullWidth
              >
                {EVENT_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Switch checked={!!form.enabled} onChange={(e) => updateField('enabled', e.target.checked)} />}
                label="Enabled (plan)"
              />
              <FormControlLabel
                control={<Switch checked={form.showInUpcoming !== false} onChange={(e) => updateField('showInUpcoming', e.target.checked)} />}
                label="Show in upcoming"
              />
              <FormControlLabel
                control={<Switch checked={!!form.leaderboardEnabled} onChange={(e) => updateField('leaderboardEnabled', e.target.checked)} />}
                label="Leaderboard"
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start (UTC)"
                type="datetime-local"
                value={toUtcInputValue(form.startDate)}
                onChange={(e) => updateField('startDate', fromUtcInputValue(e.target.value))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End (UTC)"
                type="datetime-local"
                value={toUtcInputValue(form.endDate)}
                onChange={(e) => updateField('endDate', fromUtcInputValue(e.target.value))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Leaderboard length (0 = all)"
              type="number"
              value={form.leaderboardLength ?? 0}
              onChange={(e) => updateField('leaderboardLength', Number(e.target.value))}
              fullWidth
            />
          </Stack>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Stack spacing={2}>
            <Autocomplete
              freeSolo
              options={datapoints}
              value={form.dataPoint || ''}
              onChange={(_, value) => updateField('dataPoint', value || '')}
              onInputChange={(_, value) => updateField('dataPoint', value || '')}
              renderInput={(params) => <TextField {...params} label="Data point" required />}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Collection type"
                value={form.dataCollectionType}
                onChange={(e) => updateField('dataCollectionType', e.target.value)}
                fullWidth
              >
                {DATA_COLLECTION_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Submission limit type"
                value={form.submissionLimitType}
                onChange={(e) => updateField('submissionLimitType', e.target.value)}
                fullWidth
              >
                {SUBMISSION_LIMIT_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Submission limit"
                type="number"
                value={form.eventSubmissionLimit ?? 0}
                onChange={(e) => updateField('eventSubmissionLimit', Number(e.target.value))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Game mode"
                value={form.gameMode || 'None'}
                onChange={(e) => updateField('gameMode', e.target.value)}
                fullWidth
              >
                {GAME_MODES.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Attempt length (seconds)"
                type="number"
                value={form.attemptLength ?? 60}
                onChange={(e) => updateField('attemptLength', Number(e.target.value))}
                fullWidth
              />
            </Stack>
            <Autocomplete
              freeSolo
              options={datapoints}
              value={form.dataPointInAttempt || ''}
              onChange={(_, value) => updateField('dataPointInAttempt', value || '')}
              onInputChange={(_, value) => updateField('dataPointInAttempt', value || '')}
              renderInput={(params) => <TextField {...params} label="Data point in attempt" />}
            />
            <TextField
              label="Target IDs (comma-separated GUIDs)"
              value={form.targetIds || ''}
              onChange={(e) => updateField('targetIds', e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Reward tiers (0-based ranks)</Typography>
              <Button startIcon={<AddIcon />} onClick={addTier} size="small">Add tier</Button>
            </Stack>
            {(form.rewardTierInfos || []).map((tier, tierIndex) => (
              <Paper key={tierIndex} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Tier {tierIndex + 1}</Typography>
                  <IconButton size="small" onClick={() => removeTier(tierIndex)} aria-label="Remove tier">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Min position"
                    type="number"
                    value={tier.minPosition ?? 0}
                    onChange={(e) => updateTier(tierIndex, { minPosition: Number(e.target.value) })}
                    size="small"
                  />
                  <TextField
                    label="Max position"
                    type="number"
                    value={tier.maxPosition ?? 0}
                    onChange={(e) => updateTier(tierIndex, { maxPosition: Number(e.target.value) })}
                    size="small"
                  />
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addTierReward(tierIndex)}>
                    Add reward
                  </Button>
                </Stack>
                {(tier.rewards || []).map((reward, rewardIndex) => (
                  <Stack key={rewardIndex} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      label="Amount"
                      type="number"
                      value={reward.amount ?? 1}
                      onChange={(e) => updateTierReward(tierIndex, rewardIndex, { amount: Number(e.target.value) })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <Autocomplete
                      options={assetOptions}
                      value={assetOptions.find((a) => a.id === reward.targetId) || null}
                      onChange={(_, asset) => updateTierReward(tierIndex, rewardIndex, { targetId: asset?.id || '' })}
                      getOptionLabel={(option) => option.label || option.id || ''}
                      renderInput={(params) => <TextField {...params} label="Asset" size="small" />}
                      sx={{ flex: 1 }}
                    />
                    <IconButton onClick={() => removeTierReward(tierIndex, rewardIndex)} aria-label="Remove reward">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Paper>
            ))}
          </Stack>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Donate / blueprint / tier list JSON blobs. Leave null when unused.
            </Typography>
            <TextField
              label="Advanced JSON"
              value={advancedJson}
              onChange={(e) => setAdvancedJson(e.target.value)}
              fullWidth
              multiline
              minRows={12}
              error={!!advancedError}
              helperText={advancedError || 'Keys: donateDayListJson, blueprintDayListJson, eventTierListJson'}
            />
          </Stack>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
